import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, CheckCircle, Baby, Heart, Calendar, Apple, Activity, Smile, AlertCircle, Globe, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { getNutritionPlan } from '@/data/nutritionData';
import axios from 'axios';
import ReminderModal from '@/components/ReminderModal';
import { HealthDisclaimer, EvidenceBadge } from '@/components/HealthDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaternalHealth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  
  const [hasSetup, setHasSetup] = useState(sectionParam === 'nutrition' ? true : false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSuggestion, setReminderSuggestion] = useState('');
  const [userStage, setUserStage] = useState(
    sectionParam === 'postpartum' ? 'postpartum_input' : 
    sectionParam === 'nutrition' ? 'pregnant' : ''
  );
  const [pregnancyData, setPregnancyData] = useState({ 
    dueDate: '', 
    currentWeek: sectionParam === 'nutrition' ? 20 : 0 
  });
  const [postpartumData, setPostpartumData] = useState({ 
    babyAge: '', 
    babyAgeUnit: 'weeks', 
    breastfeeding: '',
    physicalRecovery: '',
    painLevel: '',
    bleedingStatus: '',
    energyLevel: '',
    emotionalWellbeing: '',
    sleepQuality: '',
    supportSystem: ''
  });
  
  const [activeModule, setActiveModule] = useState(sectionParam === 'nutrition' ? 'nutrition' : 'dashboard');
  const [view, setView] = useState('input');
  
  const [nutritionData, setNutritionData] = useState({ culture: 'West_African' });
  const [symptomData, setSymptomData] = useState({ nausea: '', fatigue: '', pain: '', mood: '' });
  const [appointmentData, setAppointmentData] = useState({ completedAppointments: [] });
  const [breastfeedingLog, setBreastfeedingLog] = useState({ feeds: [] });
  const [mentalData, setMentalData] = useState({ mood: '', stress: '' });
  const [dailyLogData, setDailyLogData] = useState({ meals: '', sleep: '', activity: '', hydration: '' });
  
  const [results, setResults] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const calculateWeek = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeksRemaining = Math.floor(diffDays / 7);
    return Math.max(1, Math.min(40, 40 - weeksRemaining));
  };

  const getTrimester = (week) => week <= 13 ? 1 : week <= 27 ? 2 : 3;

  const handleSetup = (stage) => {
    setUserStage(stage);
    if (stage === 'preconception') {
      setHasSetup(true);
      setActiveModule('dashboard');
      toast.success('Pre-conception planning started! 🌸');
      checkAlerts(stage);
    }
  };

  const handlePregnancySetup = (e) => {
    e.preventDefault();
    if (!pregnancyData.dueDate) {
      toast.error('Please enter your due date');
      return;
    }
    const week = calculateWeek(pregnancyData.dueDate);
    setPregnancyData({ ...pregnancyData, currentWeek: week });
    setHasSetup(true);
    setActiveModule('dashboard');
    toast.success(`Week ${week} - Your pregnancy journey begins! 🤰`);
    checkAlerts('pregnant', week);
  };

  const handlePostpartumSetup = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!postpartumData.babyAge || !postpartumData.breastfeeding ||
        !postpartumData.physicalRecovery || !postpartumData.painLevel ||
        !postpartumData.bleedingStatus || !postpartumData.energyLevel ||
        !postpartumData.emotionalWellbeing || !postpartumData.sleepQuality ||
        !postpartumData.supportSystem) {
      toast.error('Please complete all required fields');
      return;
    }
    
    const analysis = analyzePostpartumWellness(postpartumData);
    
    setResults(analysis);
    setHasSetup(true);
    setActiveModule('postpartum-results');
    setView('results');
    toast.success('Postpartum assessment complete! 💝');
  };

  const analyzePostpartumWellness = (data) => {
    const recommendations = [];
    const positiveIndicators = [];
    const concernAreas = [];
    let overallRecovery = 'Good';
    let concernScore = 0;

    // Calculate baby's age in weeks for context
    const babyAgeInWeeks = data.babyAgeUnit === 'months' 
      ? parseInt(data.babyAge) * 4 
      : parseInt(data.babyAge);

    // Physical Recovery Analysis
    if (data.physicalRecovery === 'Poor' || data.physicalRecovery === 'Very Difficult') {
      concernScore += 3;
      concernAreas.push('🚨 Challenging physical recovery');
      recommendations.push({
        icon: '🩺',
        area: 'Physical Recovery',
        advice: 'Difficult recovery detected',
        actions: [
          'Contact your OB/GYN or midwife immediately',
          'Rest as much as possible when baby sleeps',
          'Accept all offered help with household tasks',
          'Do not lift anything heavier than your baby',
          'Monitor for signs of infection (fever, unusual discharge)'
        ]
      });
    } else if (data.physicalRecovery === 'Slow Progress') {
      concernScore += 1;
      concernAreas.push('⚠️ Slower than expected recovery');
      recommendations.push({
        icon: '💪',
        area: 'Physical Recovery',
        advice: 'Recovery is progressing slowly',
        actions: [
          'Be patient - every body heals differently',
          'Gentle walking when cleared by doctor',
          'Pelvic floor exercises (Kegels) when ready',
          'Attend postpartum check-up appointment',
          'Stay hydrated and eat nutritious meals'
        ]
      });
    } else {
      positiveIndicators.push('✅ Good physical recovery progress');
    }

    // Pain Level Analysis
    if (data.painLevel === 'Severe (8-10)') {
      concernScore += 3;
      concernAreas.push('🚨 Severe pain levels');
      recommendations.push({
        icon: '⚠️',
        area: 'Pain Management',
        advice: 'Severe pain requires immediate attention',
        actions: [
          'Contact your healthcare provider TODAY',
          'Severe pain is not normal - seek help',
          'Take prescribed pain medication as directed',
          'Apply ice packs to perineal area if applicable',
          'Do not ignore worsening or new pain'
        ]
      });
    } else if (data.painLevel === 'Moderate (5-7)') {
      concernScore += 1;
      recommendations.push({
        icon: '💊',
        area: 'Pain Management',
        advice: 'Moderate pain is common but manageable',
        actions: [
          'Take pain relief as prescribed',
          'Use ice packs for perineal discomfort',
          'Try warm sitz baths',
          'Mention ongoing pain at your postpartum visit',
          'Pain should gradually decrease over time'
        ]
      });
    } else if (data.painLevel === 'Mild (1-4)') {
      positiveIndicators.push('✅ Manageable pain levels');
    }

    // Bleeding Status Analysis
    if (data.bleedingStatus === 'Heavy with Clots' || data.bleedingStatus === 'Increased Bleeding') {
      concernScore += 3;
      concernAreas.push('🚨 Concerning bleeding pattern');
      recommendations.push({
        icon: '🩸',
        area: 'Postpartum Bleeding',
        advice: 'Heavy or increasing bleeding needs evaluation',
        actions: [
          'Call your doctor/midwife IMMEDIATELY',
          'If soaking through a pad per hour, go to ER',
          'Large clots (bigger than golf ball) = urgent care',
          'Lie down and rest',
          'Bleeding should decrease, not increase over time'
        ]
      });
    } else if (data.bleedingStatus === 'Normal Lochia') {
      positiveIndicators.push('✅ Normal postpartum bleeding');
      recommendations.push({
        icon: '📋',
        area: 'Postpartum Bleeding',
        advice: 'Lochia is progressing normally',
        actions: [
          'Expect bleeding for 4-6 weeks postpartum',
          'Color changes from red → pink → white is normal',
          'Use pads, not tampons',
          'Call doctor if bright red bleeding returns after slowing',
          'Track changes to discuss at postpartum appointment'
        ]
      });
    }

    // Energy Level Analysis
    if (data.energyLevel === 'Exhausted') {
      concernScore += 2;
      concernAreas.push('⚠️ Severe exhaustion');
      recommendations.push({
        icon: '😴',
        area: 'Energy & Fatigue',
        advice: 'Extreme exhaustion is impacting your wellbeing',
        actions: [
          'Sleep when baby sleeps - prioritize rest',
          'Ask partner/family to take night feeds if possible',
          'Check iron levels - postpartum anemia is common',
          'Accept help with meals and chores',
          'Mention fatigue at postpartum check (may be thyroid)'
        ]
      });
    } else if (data.energyLevel === 'Low') {
      concernScore += 1;
      recommendations.push({
        icon: '⚡',
        area: 'Energy & Fatigue',
        advice: 'Low energy is very common postpartum',
        actions: [
          'Rest whenever possible',
          'Eat small, frequent, nutritious meals',
          'Stay hydrated',
          'Short walks for gentle energy boost',
          'Energy should gradually improve over weeks'
        ]
      });
    } else {
      positiveIndicators.push('✅ Reasonable energy levels');
    }

    // Emotional Wellbeing Analysis
    if (data.emotionalWellbeing === 'Very Struggling' || data.emotionalWellbeing === 'Anxious/Depressed') {
      concernScore += 4;
      concernAreas.push('🚨 Emotional distress detected');
      recommendations.push({
        icon: '💜',
        area: 'Postpartum Mental Health',
        advice: 'You may be experiencing postpartum depression or anxiety',
        actions: [
          'Call your doctor/midwife - this is a medical emergency',
          'Postpartum depression affects 1 in 7 mothers',
          'Treatment works - medication and therapy are safe',
          'National Maternal Mental Health Hotline: 1-833-943-5746',
          'You are not alone, and this is NOT your fault'
        ]
      });
    } else if (data.emotionalWellbeing === 'Overwhelmed') {
      concernScore += 2;
      concernAreas.push('⚠️ Feeling overwhelmed');
      recommendations.push({
        icon: '🫂',
        area: 'Emotional Support',
        advice: 'Feeling overwhelmed is common but you need support',
        actions: [
          'Talk to your partner, family, or friends',
          'Join a postpartum support group',
          'Be honest with your healthcare provider',
          'Baby blues (weeks 1-2) vs PPD (persistent beyond 2 weeks)',
          'Practice self-compassion - you\'re doing your best'
        ]
      });
    } else if (data.emotionalWellbeing === 'Adjusting') {
      positiveIndicators.push('✅ Normal emotional adjustment');
      recommendations.push({
        icon: '🌸',
        area: 'Emotional Health',
        advice: 'Normal adjustment to motherhood',
        actions: [
          'Some ups and downs are expected',
          'Continue talking about your feelings',
          'Connect with other new moms',
          'Make time for self-care, even if brief',
          'Monitor your mood - seek help if it worsens'
        ]
      });
    }

    // Sleep Quality Analysis
    if (data.sleepQuality === 'Extremely Poor') {
      concernScore += 2;
      concernAreas.push('⚠️ Severe sleep deprivation');
      recommendations.push({
        icon: '🌙',
        area: 'Sleep & Rest',
        advice: 'Severe sleep deprivation affects recovery',
        actions: [
          'Sleep during baby\'s longest sleep stretch',
          'Ask partner to take some night feedings',
          'Lower standards for housework',
          'Consider safe co-sleeping guidelines if breastfeeding',
          'Chronic sleep deprivation impacts mental health'
        ]
      });
    } else if (data.sleepQuality === 'Poor') {
      recommendations.push({
        icon: '😴',
        area: 'Sleep & Rest',
        advice: 'Poor sleep is common with a newborn',
        actions: [
          'Nap when baby naps',
          'Shift system with partner if possible',
          'Keep lights dim during night feeds',
          'Sleep will gradually improve',
          'Ask for help so you can rest'
        ]
      });
    }

    // Support System Analysis
    if (data.supportSystem === 'No Support') {
      concernScore += 3;
      concernAreas.push('🚨 Lack of support system');
      recommendations.push({
        icon: '🤝',
        area: 'Support Network',
        advice: 'Having no support makes recovery much harder',
        actions: [
          'Reach out to local postpartum doula services',
          'Join new parent support groups (online or in-person)',
          'Contact social services for assistance programs',
          'Ask hospital/birthing center for community resources',
          'Online communities can provide emotional support'
        ]
      });
    } else if (data.supportSystem === 'Minimal Support') {
      concernScore += 1;
      recommendations.push({
        icon: '👥',
        area: 'Building Support',
        advice: 'More support would help your recovery',
        actions: [
          'Identify one person who can help regularly',
          'Join mother-baby groups',
          'Consider postpartum doula or helper',
          'Be specific when asking for help',
          'Virtual support groups are valuable too'
        ]
      });
    } else {
      positiveIndicators.push('✅ Good support system in place');
    }

    // Breastfeeding-Specific Support
    if (data.breastfeeding === 'Yes' || data.breastfeeding === 'Mixed') {
      recommendations.push({
        icon: '🍼',
        area: 'Breastfeeding Support',
        advice: 'Breastfeeding guidance and resources',
        actions: [
          'Contact a lactation consultant if struggling',
          'Stay very well hydrated (8-10 glasses water daily)',
          'Eat nutritious, frequent meals',
          'Rest - milk production needs your energy',
          'La Leche League offers free support: llli.org'
        ]
      });
    }

    // General Postpartum Care
    recommendations.push({
      icon: '🩺',
      area: 'Postpartum Appointments',
      advice: 'Essential healthcare follow-ups',
      actions: [
        babyAgeInWeeks < 6 ? 'Schedule 6-week postpartum check-up' : 'Attend your 6-week postpartum appointment',
        'Pelvic floor assessment (important even without symptoms)',
        'Discuss contraception options',
        'Mental health screening',
        'Address any ongoing concerns'
      ]
    });

    // Physical Activity Guidance
    if (babyAgeInWeeks >= 6) {
      recommendations.push({
        icon: '🚶‍♀️',
        area: 'Gentle Movement',
        advice: 'Returning to physical activity',
        actions: [
          'Start with gentle walks',
          'Get clearance from doctor before exercising',
          'Pelvic floor PT before high-impact activities',
          'Listen to your body',
          'Gradual progression is key'
        ]
      });
    }

    // Overall Recovery Assessment
    if (concernScore >= 8) {
      overallRecovery = 'Needs Urgent Attention';
    } else if (concernScore >= 4) {
      overallRecovery = 'Needs Support';
    } else {
      overallRecovery = 'Progressing Well';
    }

    return {
      overallRecovery,
      concernScore,
      positiveIndicators,
      concernAreas,
      recommendations,
      babyAgeInWeeks
    };
  };

  const checkAlerts = (stage, week = 0) => {
    const newAlerts = [];
    if (stage === 'pregnant' && week >= 12 && appointmentData.completedAppointments.length === 0) {
      newAlerts.push({ type: 'warning', title: 'Antenatal Visit Due', message: 'Schedule your first trimester check-up.' });
    }
    if (stage === 'postpartum' && postpartumData.breastfeeding !== 'No' && breastfeedingLog.feeds.length === 0) {
      newAlerts.push({ type: 'info', title: 'Feeding Tracker', message: 'Start logging feeding times.' });
    }
    setAlerts(newAlerts);
  };

  const handleNutritionSubmit = (e) => {
    e.preventDefault();
    const trimester = userStage === 'pregnant' ? getTrimester(pregnancyData.currentWeek) : 0;
    const plan = getNutritionPlan(userStage, trimester, nutritionData.culture);
    setResults({ nutritionPlan: plan });
    setView('results');
    toast.success('Nutrition plan ready! 🍎');
  };

  const handleSymptomSubmit = (e) => {
    e.preventDefault();
    const assessment = assessSymptoms(symptomData, userStage, pregnancyData.currentWeek);
    setResults({ symptomAssessment: assessment });
    setView('results');
    toast.success('Symptom assessment complete!');
  };

  const assessSymptoms = (symptoms, stage, week = 0) => {
    const feedback = [];
    if (symptoms.nausea === 'Severe' && stage === 'pregnant' && week <= 13) {
      feedback.push({ type: 'normal', message: 'Severe nausea is common in first trimester. Stay hydrated. If you can\'t keep fluids down, contact your doctor.' });
    } else if (symptoms.nausea === 'Severe') {
      feedback.push({ type: 'warning', message: 'Severe nausea at this stage needs medical attention.' });
    }
    if (symptoms.fatigue === 'Extreme') {
      feedback.push({ type: 'info', message: 'Extreme fatigue needs monitoring. Ensure adequate iron intake and rest.' });
    }
    if (symptoms.pain === 'Severe') {
      feedback.push({ type: 'alert', message: 'Severe pain requires immediate medical attention. Contact your healthcare provider.' });
    }
    if (symptoms.mood === 'Very Low') {
      feedback.push({ type: 'warning', message: 'Very low mood may indicate depression. Please speak with your healthcare provider.' });
    }
    if (feedback.length === 0) feedback.push({ type: 'positive', message: 'Your symptoms are within normal range.' });
    return feedback;
  };

  const handleAppointmentView = () => {
    const schedule = getAppointmentSchedule(pregnancyData.currentWeek);
    setResults({ appointments: schedule });
    setView('results');
    toast.success('Appointment schedule loaded!');
  };

  const getAppointmentSchedule = (currentWeek) => [
    { week: '6-10', type: 'First Antenatal Visit', status: appointmentData.completedAppointments.includes('first') ? 'completed' : currentWeek >= 6 ? 'due' : 'upcoming', priority: 'High' },
    { week: '11-14', type: 'Dating Scan', status: appointmentData.completedAppointments.includes('dating') ? 'completed' : currentWeek >= 11 ? 'due' : 'upcoming', priority: 'High' },
    { week: '18-21', type: 'Anatomy Scan', status: appointmentData.completedAppointments.includes('anatomy') ? 'completed' : currentWeek >= 18 ? 'due' : 'upcoming', priority: 'High' },
    { week: '28', type: 'Glucose Test', status: appointmentData.completedAppointments.includes('glucose') ? 'completed' : currentWeek >= 28 ? 'due' : 'upcoming', priority: 'High' },
    { week: '36-40', type: 'Weekly Checks', status: currentWeek >= 36 ? 'due' : 'upcoming', priority: 'High' }
  ];

  const markAppointmentComplete = (type) => {
    setAppointmentData(prev => ({ ...prev, completedAppointments: [...prev.completedAppointments, type] }));
    toast.success('Appointment marked complete! ✓');
    const schedule = getAppointmentSchedule(pregnancyData.currentWeek);
    setResults({ appointments: schedule });
  };

  const handleBreastfeedingLog = (e) => {
    e.preventDefault();
    const now = new Date();
    const newFeed = { time: now.toLocaleTimeString(), date: now.toLocaleDateString() };
    setBreastfeedingLog(prev => ({ feeds: [newFeed, ...prev.feeds.slice(0, 9)] }));
    const feedback = { frequency: breastfeedingLog.feeds.length + 1 < 8 ? 'Low' : 'Good', tips: ['Newborns feed 8-12 times in 24 hours', 'Stay hydrated', 'Rest when baby sleeps'] };
    setResults({ breastfeedingFeedback: feedback });
    setView('results');
    toast.success('Feeding logged! 🍼');
  };

  const handleMentalSubmit = (e) => {
    e.preventDefault();
    if (!mentalData.mood || !mentalData.stress) {
      toast.error('Please complete mood and stress fields');
      return;
    }
    const support = getMentalHealthSupport(mentalData, userStage);
    setResults({ mentalSupport: support });
    setView('results');
    toast.success('Mental health insights ready! 💙');
  };

  const getMentalHealthSupport = (data, stage) => {
    const tips = [];
    let riskLevel = 'Low';
    if (data.mood === 'Very Low' && data.stress === 'Very High') {
      riskLevel = 'High';
      tips.push({ area: 'Urgent Support', advice: 'You may need professional support', actions: ['Contact healthcare provider', 'Call mental health helpline'] });
    } else if (data.mood === 'Low' || data.stress === 'High') {
      riskLevel = 'Moderate';
      tips.push({ area: 'Emotional Wellbeing', advice: 'Mood changes are common', actions: ['Gentle exercise', 'Join support group', 'Relaxation techniques'] });
    }
    if (stage === 'postpartum') tips.push({ area: 'Postnatal Support', advice: 'Postpartum can be emotionally challenging', actions: ['Accept help', 'Prioritize rest', 'Monitor for postnatal depression'] });
    tips.push({ area: 'Self-Care', advice: 'Mental health is essential', actions: ['Sleep when possible', 'Maintain connections', 'Practice mindfulness'] });
    return { riskLevel, tips };
  };

  const handleDailyLogSubmit = (e) => {
    e.preventDefault();
    const feedback = analyzeDailyLog(dailyLogData);
    setResults({ dailyFeedback: feedback });
    setView('results');
    toast.success('Daily log recorded! 📝');
  };

  const analyzeDailyLog = (log) => {
    const feedback = [];
    if (log.meals === 'Excellent') feedback.push({ type: 'positive', message: 'Excellent nutrition! 🌟' });
    else if (log.meals === 'Poor') feedback.push({ type: 'warning', message: 'Try more balanced meals.' });
    if (log.sleep === 'Poor') feedback.push({ type: 'info', message: 'Use pregnancy pillows, avoid screens before bed.' });
    else if (log.sleep === 'Good') feedback.push({ type: 'positive', message: 'Good sleep quality! 😴' });
    if (log.activity === 'None') feedback.push({ type: 'info', message: 'Try gentle walking or prenatal yoga.' });
    else if (log.activity === 'Moderate') feedback.push({ type: 'positive', message: 'Great job staying active! 🚶‍♀️' });
    if (log.hydration === 'Low') feedback.push({ type: 'warning', message: 'Aim for 8-10 glasses of water daily.' });
    else if (log.hydration === 'Good') feedback.push({ type: 'positive', message: 'Excellent hydration! 💧' });
    return feedback;
  };

  const resetModule = () => { setView('input'); setResults(null); };

  const handleSaveReminder = async (reminderData) => {
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      await axios.post(`${API}/health-reminders`, reminderData, {
        withCredentials: true,
      });
      toast.success('Reminder set! 🔔 Check "My Reminders" to manage it.');
      setShowReminderModal(false);
    } catch (error) {
      console.error('Error saving reminder:', error);
      toast.error('Failed to save reminder');
    }
  };

  const openReminderModal = (suggestion) => {
    setReminderSuggestion(suggestion);
    setShowReminderModal(true);
  };

  if (!hasSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
          <div className="mb-6"><h1 className="text-3xl font-bold text-gray-900 mb-2">Maternal Health v2</h1><p className="text-gray-600">Your personalized pregnancy and motherhood journey</p></div>
          {!userStage && (
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Baby className="h-5 w-5 text-pink-600" />What stage are you in?</CardTitle></CardHeader><CardContent className="space-y-3">
              <button onClick={() => handleSetup('preconception')} className="w-full p-4 rounded-lg border-2 border-gray-300 hover:border-purple-600 hover:bg-purple-50 text-left transition-all"><h3 className="font-bold text-gray-900">Trying to Conceive</h3><p className="text-sm text-gray-600">Pre-conception planning</p></button>
              <button onClick={() => setUserStage('pregnant_input')} className="w-full p-4 rounded-lg border-2 border-gray-300 hover:border-pink-600 hover:bg-pink-50 text-left transition-all"><h3 className="font-bold text-gray-900">Pregnant</h3><p className="text-sm text-gray-600">Track pregnancy week by week</p></button>
              <button onClick={() => setUserStage('postpartum_input')} className="w-full p-4 rounded-lg border-2 border-gray-300 hover:border-blue-600 hover:bg-blue-50 text-left transition-all"><h3 className="font-bold text-gray-900">Postpartum / Breastfeeding</h3><p className="text-sm text-gray-600">Recovery and baby care</p></button>
            </CardContent></Card>
          )}
          {userStage === 'pregnant_input' && (
            <form onSubmit={handlePregnancySetup} className="space-y-5"><Card><CardHeader><CardTitle>Pregnancy Details</CardTitle></CardHeader><CardContent><label className="block text-sm font-medium text-gray-700 mb-2">Expected Due Date *</label><Input type="date" value={pregnancyData.dueDate} onChange={(e) => setPregnancyData({ ...pregnancyData, dueDate: e.target.value })} min={new Date().toISOString().split('T')[0]} required /></CardContent></Card><Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700 h-12 text-lg font-bold">Start Pregnancy Journey 🤰</Button><Button type="button" variant="outline" className="w-full" onClick={() => setUserStage('')}>Back</Button></form>
          )}
          {userStage === 'postpartum_input' && (
            <form onSubmit={handlePostpartumSetup} className="space-y-5">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Baby's Age *</CardTitle>
                  <p className="text-sm text-gray-600">How old is your baby?</p>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input 
                      type="number" 
                      value={postpartumData.babyAge} 
                      onChange={(e) => setPostpartumData({ ...postpartumData, babyAge: e.target.value })} 
                      placeholder="Enter number" 
                      min="0" 
                      required 
                      className="flex-1"
                    />
                    <select 
                      value={postpartumData.babyAgeUnit} 
                      onChange={(e) => setPostpartumData({ ...postpartumData, babyAgeUnit: e.target.value })} 
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Breastfeeding Status *</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    {['Yes', 'No', 'Mixed'].map(option => (
                      <button 
                        key={option} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, breastfeeding: option })} 
                        className={`p-3 rounded-lg border-2 font-medium transition-all ${
                          postpartumData.breastfeeding === option 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Physical Recovery *</CardTitle>
                  <p className="text-sm text-gray-600">How is your body healing?</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['Good Progress', 'Slow Progress', 'Poor', 'Very Difficult'].map(recovery => (
                      <button 
                        key={recovery} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, physicalRecovery: recovery })} 
                        className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                          postpartumData.physicalRecovery === recovery 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {recovery}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Pain Level *</CardTitle>
                  <p className="text-sm text-gray-600">Current pain intensity (0-10 scale)</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['None (0)', 'Mild (1-4)', 'Moderate (5-7)', 'Severe (8-10)'].map(pain => (
                      <button 
                        key={pain} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, painLevel: pain })} 
                        className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                          postpartumData.painLevel === pain 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {pain}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Bleeding Status *</CardTitle>
                  <p className="text-sm text-gray-600">Postpartum bleeding (lochia)</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['Normal Lochia', 'Light/Minimal', 'Heavy with Clots', 'Increased Bleeding'].map(bleeding => (
                      <button 
                        key={bleeding} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, bleedingStatus: bleeding })} 
                        className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                          postpartumData.bleedingStatus === bleeding 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {bleeding}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Energy Level *</CardTitle>
                  <p className="text-sm text-gray-600">How are you feeling physically?</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {['Exhausted', 'Low', 'Moderate', 'Good'].map(energy => (
                      <button 
                        key={energy} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, energyLevel: energy })} 
                        className={`p-3 rounded-lg border-2 font-medium transition-all ${
                          postpartumData.energyLevel === energy 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {energy}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Emotional Wellbeing *</CardTitle>
                  <p className="text-sm text-gray-600">How are you feeling emotionally?</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['Doing Well', 'Adjusting', 'Overwhelmed', 'Anxious/Depressed', 'Very Struggling'].map(mood => (
                      <button 
                        key={mood} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, emotionalWellbeing: mood })} 
                        className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                          postpartumData.emotionalWellbeing === mood 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {mood}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sleep Quality *</CardTitle>
                  <p className="text-sm text-gray-600">How well are you sleeping?</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {['Extremely Poor', 'Poor', 'Fair', 'Decent'].map(sleep => (
                      <button 
                        key={sleep} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, sleepQuality: sleep })} 
                        className={`p-3 rounded-lg border-2 font-medium transition-all ${
                          postpartumData.sleepQuality === sleep 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {sleep}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Support System *</CardTitle>
                  <p className="text-sm text-gray-600">Do you have help and support?</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['No Support', 'Minimal Support', 'Moderate Support', 'Strong Support'].map(support => (
                      <button 
                        key={support} 
                        type="button" 
                        onClick={() => setPostpartumData({ ...postpartumData, supportSystem: support })} 
                        className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                          postpartumData.supportSystem === support 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        {support}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg font-bold">
                Get Postpartum Wellness Insights 💝
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => { setUserStage(''); navigate('/family-health'); }}>
                Back
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (activeModule === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
          <div className="mb-6"><h1 className="text-2xl font-bold text-gray-900">{userStage === 'preconception' && 'Pre-conception Planning'}{userStage === 'pregnant' && `Week ${pregnancyData.currentWeek} - Trimester ${getTrimester(pregnancyData.currentWeek)}`}{userStage === 'postpartum' && `Postpartum - Baby is ${postpartumData.babyAge} ${postpartumData.babyAgeUnit}`}</h1><p className="text-gray-600">{userStage === 'preconception' && 'Preparing your body for pregnancy'}{userStage === 'pregnant' && `${40 - pregnancyData.currentWeek} weeks to go`}{userStage === 'postpartum' && `Breastfeeding: ${postpartumData.breastfeeding}`}</p></div>
          {alerts.length > 0 && (<div className="mb-6 space-y-3">{alerts.map((alert, idx) => (<Card key={idx} className={`border-2 ${alert.type === 'warning' ? 'border-amber-300 bg-amber-50' : 'border-blue-300 bg-blue-50'}`}><CardContent className="pt-4"><div className="flex items-start gap-3"><AlertCircle className={`h-5 w-5 ${alert.type === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} /><div><h4 className="font-bold text-gray-900">{alert.title}</h4><p className="text-sm text-gray-700">{alert.message}</p></div></div></CardContent></Card>))}</div>)}
          {userStage === 'pregnant' && (<div className="grid grid-cols-2 gap-4 mb-6"><Card><CardContent className="pt-6 text-center"><Calendar className="h-8 w-8 text-pink-600 mx-auto mb-2" /><p className="text-sm text-gray-600">Current Week</p><p className="text-2xl font-bold text-gray-900">{pregnancyData.currentWeek}</p></CardContent></Card><Card><CardContent className="pt-6 text-center"><CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" /><p className="text-sm text-gray-600">Appointments</p><p className="text-2xl font-bold text-gray-900">{appointmentData.completedAppointments.length}</p></CardContent></Card></div>)}
          <h2 className="text-lg font-bold text-gray-900 mb-4">Health Modules</h2>
          <div className="grid grid-cols-1 gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('nutrition'); setView('input');}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Apple className="h-10 w-10 text-green-600" /><div><h3 className="font-bold text-gray-900">Nutrition Guide</h3><p className="text-sm text-gray-600">Stage-appropriate meal plans</p></div></div></CardContent></Card>
            {userStage !== 'preconception' && (<><Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('symptoms'); setView('input');}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Heart className="h-10 w-10 text-red-600" /><div><h3 className="font-bold text-gray-900">Symptom Tracker</h3><p className="text-sm text-gray-600">Log and assess symptoms</p></div></div></CardContent></Card>
            {userStage === 'pregnant' && (<Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('appointments'); handleAppointmentView();}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Calendar className="h-10 w-10 text-purple-600" /><div><h3 className="font-bold text-gray-900">Appointments</h3><p className="text-sm text-gray-600">Antenatal visit schedule</p></div></div></CardContent></Card>)}
            {userStage === 'postpartum' && postpartumData.breastfeeding !== 'No' && (<Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('breastfeeding'); setView('input');}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Baby className="h-10 w-10 text-blue-600" /><div><h3 className="font-bold text-gray-900">Breastfeeding Tracker</h3><p className="text-sm text-gray-600">Log feeding times</p></div></div></CardContent></Card>)}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('mental'); setView('input');}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Smile className="h-10 w-10 text-yellow-600" /><div><h3 className="font-bold text-gray-900">Mental Health</h3><p className="text-sm text-gray-600">Emotional wellbeing support</p></div></div></CardContent></Card>
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveModule('daily'); setView('input');}}><CardContent className="pt-6"><div className="flex items-center gap-4"><Activity className="h-10 w-10 text-orange-600" /><div><h3 className="font-bold text-gray-900">Daily Health Log</h3><p className="text-sm text-gray-600">Track daily wellness</p></div></div></CardContent></Card></>)}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'results' && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => setActiveModule('dashboard')} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back to Overview</Button>
          <h1 className="text-2xl font-bold text-gray-900 mb-6">{activeModule === 'nutrition' && 'Nutrition Plan'}{activeModule === 'symptoms' && 'Symptom Assessment'}{activeModule === 'appointments' && 'Appointment Schedule'}{activeModule === 'breastfeeding' && 'Feeding Feedback'}{activeModule === 'mental' && 'Mental Health Support'}{activeModule === 'daily' && 'Daily Health Feedback'}{activeModule === 'postpartum-results' && 'Postpartum Wellness Assessment'}</h1>
          <Card className="mb-6 bg-green-50 border-green-200"><CardContent className="pt-6"><div className="flex items-center gap-3"><CheckCircle className="h-10 w-10 text-green-600" /><div><h3 className="font-bold text-gray-900">Analysis Complete!</h3><p className="text-sm text-gray-600">Here are your personalized recommendations</p></div></div></CardContent></Card>
          {activeModule === 'nutrition' && results.nutritionPlan && (
            <div className="space-y-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <h4 className="font-bold text-gray-900 mb-2">
                    Stage: {userStage === 'preconception' ? 'Pre-conception' : userStage === 'pregnant' ? `Trimester ${getTrimester(pregnancyData.currentWeek)}` : 'Postpartum'}
                  </h4>
                  <p className="text-sm text-gray-700">Culture: {nutritionData.culture}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recommended Foods</CardTitle>
                  <p className="text-sm text-gray-600">Include these nutrient-rich foods in your diet</p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {results.nutritionPlan.foods.map((food, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        {food}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Key Nutrients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {results.nutritionPlan.nutrients.map((nutrient, idx) => (
                      <p key={idx} className="text-sm text-gray-700 font-medium">• {nutrient}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {results.nutritionPlan.dailyMeals && (
                <Card className="bg-gradient-to-r from-green-50 to-blue-50">
                  <CardHeader>
                    <CardTitle className="text-lg">3-Day Sample Meal Plan</CardTitle>
                    <p className="text-sm text-gray-600">Portion sizes and variety for balanced nutrition</p>
                  </CardHeader>
                  <CardContent>
                    {['day1', 'day2', 'day3'].map((day, dayIdx) => {
                      const dayPlan = results.nutritionPlan.dailyMeals[day];
                      if (!dayPlan) return null;
                      
                      return (
                        <div key={day} className="mb-6 last:mb-0">
                          <h4 className="font-bold text-gray-900 mb-3 text-base">Day {dayIdx + 1}</h4>
                          <div className="space-y-3">
                            <div className="bg-white p-3 rounded-lg border border-green-200">
                              <p className="text-xs font-semibold text-green-700 mb-1">🌅 BREAKFAST</p>
                              <p className="text-sm text-gray-800">{dayPlan.breakfast}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-yellow-200">
                              <p className="text-xs font-semibold text-yellow-700 mb-1">☕ MID-MORNING SNACK</p>
                              <p className="text-sm text-gray-800">{dayPlan.midMorning}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-blue-200">
                              <p className="text-xs font-semibold text-blue-700 mb-1">🍽️ LUNCH</p>
                              <p className="text-sm text-gray-800">{dayPlan.lunch}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-orange-200">
                              <p className="text-xs font-semibold text-orange-700 mb-1">🍎 AFTERNOON SNACK</p>
                              <p className="text-sm text-gray-800">{dayPlan.afternoon}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-purple-200">
                              <p className="text-xs font-semibold text-purple-700 mb-1">🌙 DINNER</p>
                              <p className="text-sm text-gray-800">{dayPlan.dinner}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-pink-200">
                              <p className="text-xs font-semibold text-pink-700 mb-1">🥛 EVENING SNACK</p>
                              <p className="text-sm text-gray-800">{dayPlan.evening}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {results.nutritionPlan.meals && !results.nutritionPlan.dailyMeals && (
                <Card>
                  <CardHeader>
                    <CardTitle>Sample Meals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.nutritionPlan.meals.map((meal, idx) => (
                        <p key={idx} className="text-sm text-gray-700">• {meal}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border-2 border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-base">Foods to Avoid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {results.nutritionPlan.avoid.map((food, idx) => (
                      <p key={idx} className="text-sm text-gray-700">• {food}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {results.nutritionPlan.breastfeeding && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-base">Breastfeeding Support</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {results.nutritionPlan.breastfeeding.map((tip, idx) => (
                        <p key={idx} className="text-sm text-gray-700">• {tip}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {activeModule === 'symptoms' && results.symptomAssessment && (<div className="space-y-4 mb-6">{results.symptomAssessment.map((item, idx) => (<Card key={idx} className={`border-2 ${item.type === 'positive' ? 'border-green-200 bg-green-50' : item.type === 'warning' ? 'border-amber-200 bg-amber-50' : item.type === 'alert' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}><CardContent className="pt-4"><p className="text-sm text-gray-900">{item.message}</p></CardContent></Card>))}</div>)}
          {activeModule === 'appointments' && results.appointments && (<div className="space-y-4 mb-6">{results.appointments.map((apt, idx) => (<Card key={idx} className={`border-2 ${apt.status === 'completed' ? 'border-green-200 bg-green-50' : apt.status === 'due' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}><CardContent className="pt-4"><div className="flex justify-between items-center"><div><h4 className="font-bold text-gray-900">{apt.type}</h4><p className="text-sm text-gray-600">Week {apt.week}</p></div><div className="flex items-center gap-2"><span className={`text-xs px-2 py-1 rounded ${apt.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{apt.priority}</span>{apt.status === 'completed' ? (<span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">✓ Done</span>) : apt.status === 'due' && (<Button size="sm" onClick={() => markAppointmentComplete(apt.type.toLowerCase().split(' ')[0])}>Mark Done</Button>)}</div></div></CardContent></Card>))}</div>)}
          {activeModule === 'breastfeeding' && results.breastfeedingFeedback && (<div className="space-y-4 mb-6"><Card><CardHeader><CardTitle>Feeding Frequency</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold text-gray-900">{results.breastfeedingFeedback.frequency}</p><p className="text-sm text-gray-600">Feeds logged: {breastfeedingLog.feeds.length}</p></CardContent></Card><Card><CardHeader><CardTitle>Tips</CardTitle></CardHeader><CardContent><div className="space-y-1">{results.breastfeedingFeedback.tips.map((tip, idx) => (<p key={idx} className="text-sm text-gray-700">• {tip}</p>))}</div></CardContent></Card></div>)}
          {activeModule === 'mental' && results.mentalSupport && (<div className="space-y-4 mb-6"><Card className={`border-2 ${results.mentalSupport.riskLevel === 'High' ? 'border-red-200 bg-red-50' : results.mentalSupport.riskLevel === 'Moderate' ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}><CardContent className="pt-4"><h4 className="font-bold text-gray-900">Risk Level: {results.mentalSupport.riskLevel}</h4></CardContent></Card>{results.mentalSupport.tips.map((tip, idx) => (<Card key={idx}><CardHeader><CardTitle className="text-base">{tip.area}</CardTitle></CardHeader><CardContent><p className="text-sm text-gray-700 mb-3"><strong>Advice:</strong> {tip.advice}</p><div className="space-y-1"><p className="text-sm font-medium text-gray-900">Actions:</p>{tip.actions.map((action, aidx) => (<p key={aidx} className="text-sm text-gray-700">• {action}</p>))}</div></CardContent></Card>))}</div>)}
          {activeModule === 'daily' && results.dailyFeedback && (<div className="space-y-4 mb-6">{results.dailyFeedback.map((item, idx) => (<Card key={idx} className={`border-2 ${item.type === 'positive' ? 'border-green-200 bg-green-50' : item.type === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'}`}><CardContent className="pt-4"><p className="text-sm text-gray-900">{item.message}</p></CardContent></Card>))}</div>)}
          {activeModule === 'postpartum-results' && results.recommendations && (
            <>
              <Card className={`mb-6 ${results.overallRecovery === 'Progressing Well' ? 'bg-green-50 border-green-300' : results.overallRecovery === 'Needs Support' ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'}`}><CardContent className="pt-6"><div className="flex items-center gap-3 mb-3"><Heart className={`h-10 w-10 ${results.overallRecovery === 'Progressing Well' ? 'text-green-600' : results.overallRecovery === 'Needs Support' ? 'text-amber-600' : 'text-red-600'}`} /><div><h3 className="font-bold text-gray-900 text-lg">Overall Recovery: {results.overallRecovery}</h3><p className="text-sm text-gray-600">{results.overallRecovery === 'Progressing Well' && 'Your recovery is on track'}{results.overallRecovery === 'Needs Support' && 'Some areas need attention and support'}{results.overallRecovery === 'Needs Urgent Attention' && 'Please contact your healthcare provider soon'}</p></div></div></CardContent></Card>
              {results.positiveIndicators && results.positiveIndicators.length > 0 && (<Card className="mb-6 bg-green-50 border-green-200"><CardHeader><CardTitle className="text-base text-green-800 flex items-center gap-2"><CheckCircle className="h-5 w-5" />Positive Indicators</CardTitle></CardHeader><CardContent><div className="space-y-2">{results.positiveIndicators.map((indicator, idx) => (<p key={idx} className="text-sm text-gray-700 font-medium">{indicator}</p>))}</div></CardContent></Card>)}
              {results.concernAreas && results.concernAreas.length > 0 && (<Card className="mb-6 bg-amber-50 border-amber-200"><CardHeader><CardTitle className="text-base text-amber-800 flex items-center gap-2"><AlertCircle className="h-5 w-5" />Areas Needing Attention</CardTitle></CardHeader><CardContent><div className="space-y-2">{results.concernAreas.map((area, idx) => (<p key={idx} className="text-sm text-gray-700 font-medium">{area}</p>))}</div></CardContent></Card>)}
              <h3 className="text-lg font-bold text-gray-900 mb-4">Personalized Care Recommendations</h3>
              <div className="space-y-4 mb-6">{results.recommendations.map((rec, idx) => (<Card key={idx}><CardHeader><CardTitle className="text-base flex items-center gap-2"><span className="text-2xl">{rec.icon}</span>{rec.area}</CardTitle></CardHeader><CardContent><p className="text-sm text-gray-700 mb-3"><strong>Insight:</strong> {rec.advice}</p><div className="space-y-1"><p className="text-sm font-medium text-gray-900">Recommended Actions:</p>{rec.actions.map((action, aidx) => (<p key={aidx} className="text-sm text-gray-700">• {action}</p>))}</div></CardContent></Card>))}</div>
            </>
          )}
          <div className="space-y-3 mb-6"><EvidenceBadge className="w-full justify-center" /><HealthDisclaimer variant="compact" /></div>
          <Card><CardContent className="pt-6 space-y-3"><Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => openReminderModal(activeModule === 'nutrition' ? 'Next Nutrition Check' : activeModule === 'symptoms' ? 'Symptom Follow-up' : 'Antenatal Appointment')}><Bell className="h-4 w-4 mr-2" />Set Health Reminder</Button><Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={resetModule}>{activeModule === 'nutrition' && 'Switch Culture'}{activeModule === 'symptoms' && 'Log New Symptoms'}{activeModule === 'appointments' && 'View Schedule Again'}{activeModule === 'breastfeeding' && 'Log Another Feed'}{activeModule === 'mental' && 'New Check-in'}{activeModule === 'daily' && 'Log Another Day'}</Button><Button className="w-full" variant="outline" onClick={() => setActiveModule('dashboard')}>Back to Overview</Button></CardContent></Card>
        </div>
        <ReminderModal isOpen={showReminderModal} onClose={() => setShowReminderModal(false)} onSave={handleSaveReminder} module="maternal" suggestedTitle={reminderSuggestion} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => setActiveModule('dashboard')} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back to Overview</Button>
        <div className="mb-6"><h1 className="text-2xl font-bold text-gray-900 mb-1">{activeModule === 'nutrition' && 'Nutrition Guide'}{activeModule === 'symptoms' && 'Symptom Tracker'}{activeModule === 'breastfeeding' && 'Breastfeeding Tracker'}{activeModule === 'mental' && 'Mental Health'}{activeModule === 'daily' && 'Daily Health Log'}</h1></div>
        {activeModule === 'nutrition' && (
          <form onSubmit={handleNutritionSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Your Stage *</CardTitle>
                <p className="text-sm text-gray-600">What stage are you currently in?</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setUserStage('preconception');
                      setPregnancyData({ ...pregnancyData, currentWeek: 0 });
                    }}
                    className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                      userStage === 'preconception'
                        ? 'border-green-600 bg-green-100 text-green-900'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    Pre-conception / Planning to Conceive
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUserStage('pregnant');
                      if (pregnancyData.currentWeek === 0) {
                        setPregnancyData({ ...pregnancyData, currentWeek: 14 });
                      }
                    }}
                    className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                      userStage === 'pregnant'
                        ? 'border-green-600 bg-green-100 text-green-900'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    Pregnant
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUserStage('postpartum');
                      setPregnancyData({ ...pregnancyData, currentWeek: 0 });
                    }}
                    className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                      userStage === 'postpartum'
                        ? 'border-green-600 bg-green-100 text-green-900'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    Postpartum / Breastfeeding
                  </button>
                </div>
              </CardContent>
            </Card>

            {userStage === 'pregnant' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Select Your Trimester *</CardTitle>
                  <p className="text-sm text-gray-600">Or enter your pregnancy week</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, currentWeek: 8 })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        getTrimester(pregnancyData.currentWeek) === 1
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      Trimester 1<br/><span className="text-xs">(Weeks 1-13)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, currentWeek: 20 })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        getTrimester(pregnancyData.currentWeek) === 2
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      Trimester 2<br/><span className="text-xs">(Weeks 14-27)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, currentWeek: 32 })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        getTrimester(pregnancyData.currentWeek) === 3
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      Trimester 3<br/><span className="text-xs">(Weeks 28-40)</span>
                    </button>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Or enter exact pregnancy week (1-40):
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="40"
                      value={pregnancyData.currentWeek || ''}
                      onChange={(e) => setPregnancyData({ ...pregnancyData, currentWeek: parseInt(e.target.value) || 0 })}
                      placeholder="Enter week number"
                    />
                  </div>
                  {pregnancyData.currentWeek > 0 && (
                    <p className="text-sm text-green-700 font-medium">
                      ✓ Week {pregnancyData.currentWeek} - Trimester {getTrimester(pregnancyData.currentWeek)}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Culture/Cuisine *</CardTitle>
                <p className="text-sm text-gray-600">Choose your preferred cuisine for personalized meal recommendations</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { label: 'West African (Nigerian/Ghanaian)', value: 'West_African' },
                    { label: 'East African (Kenyan/Ethiopian/Ugandan)', value: 'East_African' },
                    { label: 'North African (Moroccan/Egyptian)', value: 'North_African' },
                    { label: 'Southern African (South African/Zimbabwean)', value: 'Southern_African' },
                    { label: 'Middle Eastern (Lebanese/Arab/Persian)', value: 'Middle_Eastern' },
                    { label: 'South Asian (Indian/Pakistani/Bangladeshi)', value: 'South_Asian' },
                    { label: 'East Asian (Chinese/Japanese/Korean)', value: 'East_Asian' },
                    { label: 'Southeast Asian (Thai/Vietnamese/Filipino/Indonesian)', value: 'Southeast_Asian' },
                    { label: 'Mexican', value: 'Mexican' },
                    { label: 'South American (Brazilian/Argentinian)', value: 'South_American' },
                    { label: 'Central American', value: 'Central_American' },
                    { label: 'Caribbean (Jamaican/Trinidadian)', value: 'Caribbean' },
                    { label: 'Mediterranean (Greek/Italian/Spanish)', value: 'Mediterranean' },
                    { label: 'UK/Irish', value: 'UK_Irish' },
                    { label: 'North American (USA/Canadian)', value: 'North_American' },
                    { label: 'Nordic (Scandinavian)', value: 'Nordic' },
                    { label: 'Eastern European (Polish/Russian)', value: 'Eastern_European' },
                    { label: 'Pacific Islander (Polynesian/Fijian)', value: 'Pacific_Islander' }
                  ].map(culture => (
                    <button
                      key={culture.value}
                      type="button"
                      onClick={() => setNutritionData({ culture: culture.value })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all flex items-center gap-3 text-left ${
                        nutritionData.culture === culture.value
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <Globe className="h-5 w-5 flex-shrink-0" />
                      <span>{culture.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg font-bold">
              View Meal Plan 🍎
            </Button>
          </form>
        )}
        {activeModule === 'symptoms' && (<form onSubmit={handleSymptomSubmit} className="space-y-5"><Card><CardHeader><CardTitle>Nausea Level</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['None', 'Mild', 'Moderate', 'Severe'].map(level => (<button key={level} type="button" onClick={() => setSymptomData({ ...symptomData, nausea: level })} className={`p-3 rounded-lg border-2 font-medium transition-all ${symptomData.nausea === level ? 'border-red-600 bg-red-100 text-red-900' : 'border-gray-300 hover:border-red-300'}`}>{level}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Fatigue Level</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['None', 'Mild', 'Moderate', 'Extreme'].map(level => (<button key={level} type="button" onClick={() => setSymptomData({ ...symptomData, fatigue: level })} className={`p-3 rounded-lg border-2 font-medium transition-all ${symptomData.fatigue === level ? 'border-red-600 bg-red-100 text-red-900' : 'border-gray-300 hover:border-red-300'}`}>{level}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Pain Level</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['None', 'Mild', 'Moderate', 'Severe'].map(level => (<button key={level} type="button" onClick={() => setSymptomData({ ...symptomData, pain: level })} className={`p-3 rounded-lg border-2 font-medium transition-all ${symptomData.pain === level ? 'border-red-600 bg-red-100 text-red-900' : 'border-gray-300 hover:border-red-300'}`}>{level}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Mood</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Good', 'Anxious', 'Low', 'Very Low'].map(mood => (<button key={mood} type="button" onClick={() => setSymptomData({ ...symptomData, mood })} className={`p-3 rounded-lg border-2 font-medium transition-all ${symptomData.mood === mood ? 'border-red-600 bg-red-100 text-red-900' : 'border-gray-300 hover:border-red-300'}`}>{mood}</button>))}</div></CardContent></Card><Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 text-lg font-bold">Submit Symptoms ❤️</Button></form>)}
        {activeModule === 'breastfeeding' && (<form onSubmit={handleBreastfeedingLog} className="space-y-5"><Card><CardHeader><CardTitle>Log Feeding Time</CardTitle></CardHeader><CardContent><p className="text-sm text-gray-700 mb-4">Click the button below to log a feeding session. Time will be recorded automatically.</p><p className="text-sm text-gray-600">Feeds logged today: {breastfeedingLog.feeds.filter(f => f.date === new Date().toLocaleDateString()).length}</p></CardContent></Card><Card><CardHeader><CardTitle>Recent Feeds</CardTitle></CardHeader><CardContent>{breastfeedingLog.feeds.length > 0 ? (<div className="space-y-2">{breastfeedingLog.feeds.slice(0, 5).map((feed, idx) => (<div key={idx} className="p-2 bg-gray-50 rounded"><p className="text-sm text-gray-900">{feed.time} - {feed.date}</p></div>))}</div>) : (<p className="text-sm text-gray-600">No feeds logged yet</p>)}</CardContent></Card><Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-bold">Log Feed Now 🍼</Button></form>)}
        {activeModule === 'mental' && (<form onSubmit={handleMentalSubmit} className="space-y-5"><Card><CardHeader><CardTitle>Current Mood</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Good', 'Anxious', 'Low', 'Very Low'].map(mood => (<button key={mood} type="button" onClick={() => setMentalData({ ...mentalData, mood })} className={`p-3 rounded-lg border-2 font-medium transition-all ${mentalData.mood === mood ? 'border-yellow-600 bg-yellow-100 text-yellow-900' : 'border-gray-300 hover:border-yellow-300'}`}>{mood}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Stress Level</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Low', 'Moderate', 'High', 'Very High'].map(level => (<button key={level} type="button" onClick={() => setMentalData({ ...mentalData, stress: level })} className={`p-3 rounded-lg border-2 font-medium transition-all ${mentalData.stress === level ? 'border-yellow-600 bg-yellow-100 text-yellow-900' : 'border-gray-300 hover:border-yellow-300'}`}>{level}</button>))}</div></CardContent></Card><Button type="submit" className="w-full bg-yellow-600 hover:bg-yellow-700 h-12 text-lg font-bold">Check Mental Health 💙</Button></form>)}
        {activeModule === 'daily' && (<form onSubmit={handleDailyLogSubmit} className="space-y-5"><Card><CardHeader><CardTitle>Meals & Nutrition</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Excellent', 'Good', 'Fair', 'Poor'].map(option => (<button key={option} type="button" onClick={() => setDailyLogData({ ...dailyLogData, meals: option })} className={`p-3 rounded-lg border-2 font-medium transition-all ${dailyLogData.meals === option ? 'border-orange-600 bg-orange-100 text-orange-900' : 'border-gray-300 hover:border-orange-300'}`}>{option}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Sleep Quality</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Good', 'Fair', 'Poor', 'Very Poor'].map(option => (<button key={option} type="button" onClick={() => setDailyLogData({ ...dailyLogData, sleep: option })} className={`p-3 rounded-lg border-2 font-medium transition-all ${dailyLogData.sleep === option ? 'border-orange-600 bg-orange-100 text-orange-900' : 'border-gray-300 hover:border-orange-300'}`}>{option}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Activity Level</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['High', 'Moderate', 'Low', 'None'].map(option => (<button key={option} type="button" onClick={() => setDailyLogData({ ...dailyLogData, activity: option })} className={`p-3 rounded-lg border-2 font-medium transition-all ${dailyLogData.activity === option ? 'border-orange-600 bg-orange-100 text-orange-900' : 'border-gray-300 hover:border-orange-300'}`}>{option}</button>))}</div></CardContent></Card><Card><CardHeader><CardTitle>Hydration</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{['Good', 'Moderate', 'Low', 'Very Low'].map(option => (<button key={option} type="button" onClick={() => setDailyLogData({ ...dailyLogData, hydration: option })} className={`p-3 rounded-lg border-2 font-medium transition-all ${dailyLogData.hydration === option ? 'border-orange-600 bg-orange-100 text-orange-900' : 'border-gray-300 hover:border-orange-300'}`}>{option}</button>))}</div></CardContent></Card><Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-lg font-bold">Submit Daily Log 📝</Button></form>)}
      </div>
    </div>
  );
};

export default MaternalHealth;